import { createSlice } from '@reduxjs/toolkit' //importing create slice
import { RootState } from '../store'
import { loadStateFromLocalStorage, saveStateToLocalStorage } from '../../utils'
import {
  fetchErrorLogs,
  getAdvisorsAction,
  getClientsAction,
  getSubscriberSummary,
  updateUsersAction,
  updateClientId,
  assignClientId,
  getClientsActivityAction,
} from '../actions/clientsActions'
import { ErrorLogItem, Subscriber } from '../types'
import { Timestamp } from 'firebase/firestore'
import { addClientAndAccessCode } from '../actions/accessCodeActions'

export type AdvisorType = {
  app_user_id: string
  email: string
  id: string
  user_name: string
}

export type AccessCodeData = {
  id: string
  expire_at: string
}

export type Permission = {
  permissions: string[]
}

export type Shared = {
  ownerId: string
  permission: Permission[]
}
export type Sharing = {
  guestId: string
  permission: Permission[]
}

export type ClientType = {
  pending_user_name?: string
  pending_email?: string
  clientId?: string
  id: string
  access_code_data: AccessCodeData[]
  app_user_id: string
  business_name: string
  created_at: string | Timestamp
  email: string
  role_type: string
  roles: string[]
  user_name: string
  advisors?: AdvisorType[]
  clients?: AdvisorType[]
  sharing?: Sharing[]
  shared?: Shared[]
  type?: 'invite code' | 'license' // Restricts the values
  verificationStatus?: 'pending' | 'active'
  status?: string
  pending?: boolean
  contract?: string // Optional field for contract reference
}

export type PendingClientType = {
  pending_user_name: string
  pending_email: string
  clientId: string
  role_type: string
  roles?: string[]
  id: string
  access_code_data: AccessCodeData[]
  business_name: string
  created_at?: Timestamp
  updated_at?: Timestamp
  pending: boolean
  verificationStatus?: 'pending' | 'active'
}

type State = {
  loading: boolean
  error: string | undefined
  clients: ClientType[] | undefined
  pendingClients: PendingClientType[] | undefined
  pendingAdvisors: PendingClientType[] | undefined
  advisors: ClientType[] | undefined
  subscribers: Record<string, Subscriber> | undefined
  errorLog: Record<string, ErrorLogItem[]> | undefined
  activeClientsSelectedTab: string
  clientsActivity: Record<string, {loginTime: number}> | undefined
  clientsWithNoSnapshots: string[]
}

const initialState: State = {
  clients: undefined,
  pendingClients: undefined,
  pendingAdvisors: undefined,
  advisors: undefined,
  loading: false,
  error: undefined,
  subscribers: undefined,
  errorLog: undefined,
  activeClientsSelectedTab: 'personal_information',
  clientsActivity: undefined,
  clientsWithNoSnapshots:[]
}

const persistedState = {
  ...initialState,
  ...loadStateFromLocalStorage<Partial<State>>('clientsState'),
}

const clientsSlice = createSlice({
  name: 'clients_slice',
  initialState: persistedState,
  reducers: {
    logout: () => {
      // Reset the other state on logout
      return initialState
    },
    setActiveClientsSelectedTab: (state: State, action): void => {
      state.activeClientsSelectedTab = action.payload
    },
    setPendingClients: (state: State, action): void => {
      state.pendingClients = action.payload
      saveStateToLocalStorage('clientsState', {
        clients: state.clients,
        pendingClients: state.pendingClients,
        advisors: state.advisors,
      })
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getClientsAction.pending, (state) => {
      state.loading = true
    })

    builder.addCase(getClientsAction.fulfilled, (state, action) => {
      state.loading = false
      if (action.payload) {
        state.error = undefined
        state.clients = undefined
        //state.clients = action.payload;
        const pendingClients: PendingClientType[] = []
        const activeClients: ClientType[] = []

        action.payload.forEach((client) => {
          if (
            client.verificationStatus === 'pending' ||
            (client.pending && client.pending === true)
          ) {
            pendingClients.push(client as PendingClientType)
          } else {
            activeClients.push(client)
          }
        })
        state.pendingClients = pendingClients
        state.clients = activeClients
        saveStateToLocalStorage('clientsState', {
          pendingClients: state.pendingClients,
          clients: state.clients,
          advisors: state.advisors,
        })
      }
    })
    builder.addCase(updateClientId.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(updateClientId.rejected, (state) => {
      state.loading = false
      state.error = 'Failed to update client ID'
    })
    builder.addCase(updateClientId.fulfilled, (state, action) => {
      state.loading = false
      const { clientDocId, clientId, oldClientId } = action.payload || {}
      if (clientDocId && clientId && state.clients) {
        // A clientId "update" is a rename of the whole partnerClientId
        // bucket (functions/v2 renameUserClientId_v2) — it cascades to
        // every client who shared the old clientId, not just this one.
        state.clients = state.clients.map((c) => {
          if (c.id === clientDocId) return { ...c, clientId }
          if (oldClientId && c.clientId === oldClientId) return { ...c, clientId }
          return c
        })
      }
    })

    builder.addCase(assignClientId.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(assignClientId.rejected, (state) => {
      state.loading = false
      state.error = 'Failed to assign client ID'
    })
    builder.addCase(assignClientId.fulfilled, (state, action) => {
      state.loading = false
      const { clientDocId, clientId } = action.payload || {}
      if (clientDocId && clientId && state.clients) {
        state.clients = state.clients.map((c) =>
          c.id === clientDocId ? { ...c, clientId } : c,
        )
      }
    })

    builder.addCase(getClientsAction.rejected, (state, action) => {
      state.loading = false
      if (typeof action.payload === 'string') {
        state.error = action.payload
      } else if (action.payload instanceof Error) {
        state.error = action.payload.message
      } else {
        state.error =
          'Error occurred while retreiving client list!'
      }
    })
    builder.addCase(getAdvisorsAction.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(getAdvisorsAction.fulfilled, (state, action) => {
      state.loading = false
      state.error = undefined
      // console.log('advisors from getAdvisorsAction', action.payload)
      if (action.payload) {
        const pendingAdvisors: PendingClientType[] = []
        const activeAdvisors: ClientType[] = []

        action.payload.forEach((advisor) => {
          if (
            advisor.verificationStatus === 'pending' ||
            (advisor.pending && advisor.pending === true)
          ) {
            pendingAdvisors.push(advisor as PendingClientType)
          } else {
            activeAdvisors.push(advisor)
          }
        })
        state.advisors = activeAdvisors
        state.pendingAdvisors = pendingAdvisors
        saveStateToLocalStorage('clientsState', {
          clients: state.clients,
          advisors: state.advisors,
          pendingClients: state.pendingClients,
          pendingAdvisors: state.pendingAdvisors,
        })
      }
    })
    builder.addCase(getAdvisorsAction.rejected, (state, action) => {
      state.loading = false
      if (typeof action.payload === 'string') {
        state.error = action.payload
      } else if (action.payload instanceof Error) {
        state.error = action.payload.message
      } else {
        state.error = 'Unknown error occurred while getting advisors list!'
      }
    })
    builder.addCase(updateUsersAction.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(updateUsersAction.fulfilled, (state, action) => {
      state.loading = false
      if (action.payload) {
        action.payload.forEach((user) => {
          if (user.role_type == 'client' && state.clients) {
            state.clients = state.clients.map((item) =>
              item.id == user.id ? user : item,
            )
          }
          if (user.role_type == 'advisor' && state.advisors) {
            state.advisors = state.advisors.map((item) =>
              item.id == user.id ? user : item,
            )
          }
        })

        saveStateToLocalStorage('clientsState', {
          clients: state.clients,
          advisors: state.advisors,
        })
      }
    })
    builder.addCase(updateUsersAction.rejected, (state, action) => {
      state.loading = false
      if (typeof action.payload === 'string') {
        state.error = action.payload
      } else if (action.payload instanceof Error) {
        state.error = action.payload.message
      } else {
        state.error = 'Unknown error occurred while updating user!'
      }
    })
    builder.addCase(getSubscriberSummary.pending, (state) => {
      state.loading = true
      state.error = undefined
    })

    builder.addCase(getSubscriberSummary.fulfilled, (state, action) => {
      state.loading = false
      state.error = undefined
      state.subscribers = action.payload
      
      state.clientsActivity = Object.entries(action.payload).reduce(
        (acc, [userId, subscriber]) => {
          const newLoginTime = new Date(subscriber.last_seen).getTime()
          const existingLoginTime = state.clientsActivity?.[userId]?.loginTime ?? 0
          
          acc[userId] = {
            loginTime: Math.max(newLoginTime, existingLoginTime)
          }
          return acc
        },
        { ...state.clientsActivity } as Record<string, { loginTime: number }>
      )
      // Optional: persist if needed
      saveStateToLocalStorage('clientsState', {
        subscribers: state.subscribers,
        clients: state.clients,
        pendingClients: state.pendingClients,
        advisors: state.advisors,
        pendingAdvisors: state.pendingAdvisors,
        clientsActivity: state.clientsActivity,
      })
    })

    builder.addCase(getSubscriberSummary.rejected, (state, action) => {
      state.loading = false
      console.error('Redux error from getSubscriberSummary:', action)

      if (typeof action.payload === 'string') {
        state.error = action.payload
      } else if (action.payload instanceof Error) {
        state.error = action.payload.message
      } else {
        state.error = 'Unknown error occurred while getting subscriber data!'
      }
    })
    builder.addCase(fetchErrorLogs.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(fetchErrorLogs.fulfilled, (state, action) => {
      state.loading = false
      state.errorLog = action.payload
    })
    builder.addCase(fetchErrorLogs.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload as string
    })
    builder.addCase(addClientAndAccessCode.fulfilled, (state, action) => {
      state.loading = false
      state.error = undefined
      if (action.payload) {
        if (action.payload.newClient.role_type === 'client') {
          // If the new client is of type 'client', add to clients
          state.pendingClients = state.pendingClients
            ? [...state.pendingClients, action.payload.newClient]
            : [action.payload.newClient]
        } else {
          state.pendingAdvisors = state.pendingAdvisors
            ? [...state.pendingAdvisors, action.payload.newClient]
            : [action.payload.newClient]
        }
        saveStateToLocalStorage('clientsState', {
          clients: state.clients,
          pendingAdvisors: state.pendingAdvisors,
          pendingClients: state.pendingClients,
          advisors: state.advisors,
          subscribers: state.subscribers,
        })
      }
    })
    builder.addCase(addClientAndAccessCode.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(addClientAndAccessCode.rejected, (state, action) => {
      state.loading = false
      if (typeof action.payload === 'string') {
        state.error = action.payload
      } else if (action.payload instanceof Error) {
        state.error = action.payload.message
      } else {
        state.error = 'Unknown error occurred while creating a new client!'
      }
    })
    builder.addCase(getClientsActivityAction.pending, (state) => {
      state.loading = true
    })

    builder.addCase(getClientsActivityAction.fulfilled, (state, action) => {
      state.loading = false
      if (action.payload) {
        state.error = undefined
        state.clientsWithNoSnapshots = action.payload.usersWithNoSnapshots || []
        
        // Merge clientsActivity, only replacing if newer
        const incomingActivity = action.payload.usersWithLastActivity
        
        state.clientsActivity = Object.entries(incomingActivity).reduce(
          (acc, [userId, activity]) => {
            const newLoginTime = activity.loginTime
            const existingLoginTime = acc[userId]?.loginTime ?? 0
            
            acc[userId] = {
              loginTime: Math.max(newLoginTime, existingLoginTime)
            }
            return acc
          },
          { ...state.clientsActivity } as Record<string, { loginTime: number }>
        )
        
        saveStateToLocalStorage('clientsState', {
          pendingClients: state.pendingClients,
          clients: state.clients,
          clientsLastActivity: state.clientsActivity,
          advisors: state.advisors,
        })
      }
    })
    builder.addCase(getClientsActivityAction.rejected, (state, action) => {
      state.loading = false
      if (typeof action.payload === 'string') {
        state.error = action.payload
      } else if (action.payload instanceof Error) {
        state.error = action.payload.message
      } else {
        state.error =
          'Error occurred while retreiving client Last activity!'
      }
    })
  },
})

export const selectAccessCodeState = (state: RootState) =>
  state.accessCode_slice
export const selectClientState = (state: RootState) => state.clients_slice
export const {
  logout,
  setActiveClientsSelectedTab,
  setPendingClients,
} = clientsSlice.actions

export default clientsSlice.reducer
